import isoMap from "@/assets/districts/konepaja-iso-map.webp.asset.json";
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Expand,
  ShoppingBag,
  Sparkles,
  UtensilsCrossed,
  X,
} from "lucide-react";

import MAP from "@/data/konepaja-map.json";

export type IsoPin = {
  id: string;
  label: string;
  address: string | null;
  lat: number;
  lng: number;
  slug?: string;
  category?: string | null;
  image?: string | null;
};

type Placed = IsoPin & { x: number; y: number; color: string; kind: PinKind; n: number; building: number; anchor?: boolean };

const MIN_ZOOM = 0.8;
const MAX_ZOOM = 4;

/** Vertical scale of the isometric extrusion (screen units per metre). */
const H_SCALE = 0.85;

type PinKind = "eat" | "shop" | "attraction" | "sauna" | "do";

const PIN_STYLE: Record<PinKind, { color: string; Icon: typeof UtensilsCrossed }> = {
  eat: { color: "#F97316", Icon: UtensilsCrossed },
  shop: { color: "#22A06B", Icon: ShoppingBag },
  attraction: { color: "#A855F7", Icon: Sparkles },
  sauna: { color: "#F0A040", Icon: Sparkles },
  do: { color: "#2E7A87", Icon: ArrowUpRight },
};

function kindOf(category?: string | null): PinKind {
  const c = (category ?? "").toLowerCase();
  if (/restaurant|bar|cafe|caf|food|eat|bistro|pizz|brew|pub/.test(c)) return "eat";
  if (/shop|store|retail|market|boutique/.test(c)) return "shop";
  if (/sauna|spa/.test(c)) return "sauna";
  if (/museum|gallery|attraction|culture|art|climb|event/.test(c)) return "attraction";
  return "do";
}

type Building = { label: string | null; h: number; brick: boolean; p: number[][] };

const BUILDINGS = MAP.buildings as Building[];
const VIEW = MAP.view as { x: number; y: number; w: number; h: number };
const ROT = (MAP.rotDeg * Math.PI) / 180;
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = Math.sin(Math.PI / 6);

/**
 * Same projection the map data was generated with: real lat/lng → local metres
 * → block rotation → 2:1 isometric screen units. Keeping it identical means
 * venue pins land on the real buildings, not on a guessed bounding box.
 */
function projectLatLng(lat: number, lng: number): { x: number; y: number } {
  const mx = (lng - MAP.lng0) * MAP.mPerDeg * Math.cos((MAP.lat0 * Math.PI) / 180);
  const my = (lat - MAP.lat0) * MAP.mPerDeg;
  const c = Math.cos(ROT);
  const s = Math.sin(ROT);
  const rx = mx * c - my * s;
  const ry = mx * s + my * c;
  return { x: (rx - ry) * COS30, y: -(rx + ry) * SIN30 };
}

/** Screen units → percentage of the SVG viewBox, for HTML overlays. */
const pctX = (x: number) => ((x - VIEW.x) / VIEW.w) * 100;
const pctY = (y: number) => ((y - VIEW.y) / VIEW.h) * 100;

/** Painter's algorithm: draw far buildings first so near ones overlap them. */
const SORTED_BUILDINGS = [...BUILDINGS].sort(
  (a, b) => Math.min(...a.p.map((q) => q[1])) - Math.min(...b.p.map((q) => q[1])),
);


/** Fallback numbered markers: the real halls, numbered like the site plan. */
const HALL_MARKERS: Placed[] = SORTED_BUILDINGS.map((b, i) => {
  const xs = b.p.map((q) => q[0]);
  const ys = b.p.map((q) => q[1]);
  const area = (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
  return {
    i,
    area,
    label: b.label,
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2 - b.h * H_SCALE * 0.6,
  };
})
  .filter((b) => b.label && b.area > 250)
  .sort((a, b) => b.area - a.area)
  .slice(0, 26)
  .sort((a, b) => a.y - b.y)
  .map((b, idx) => ({
    id: `hall-${b.i}`,
    label: b.label as string,
    address: b.label as string,
    lat: 0,
    lng: 0,
    kind: "do" as PinKind,
    color: PIN_STYLE.do.color,
    x: b.x,
    y: b.y,
    n: idx + 1,
    building: b.i,
  }));



/** Ray-casting test in projected screen units (footprints are ground level). */
function pointInPoly(x: number, y: number, poly: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Which building footprint a pin sits on (or the nearest one within reach). */
function buildingAt(x: number, y: number): number {
  let nearest = -1;
  let bestD = 34;
  for (let i = 0; i < SORTED_BUILDINGS.length; i++) {
    const b = SORTED_BUILDINGS[i];
    if (pointInPoly(x, y, b.p)) return i;
    for (const [px, py] of b.p) {
      const d = Math.hypot(px - x, py - y);
      if (d < bestD) {
        bestD = d;
        nearest = i;
      }
    }
  }
  return nearest;
}

function project(pins: IsoPin[]): Placed[] {
  return pins
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .map((p, i) => {
      const { x, y } = projectLatLng(p.lat, p.lng);
      const kind = kindOf(p.category);
      return {
        ...p,
        kind,
        color: PIN_STYLE[kind].color,
        x,
        y,
        n: i + 1,
        building: buildingAt(x, y),
      };
    });
}

/**
 * Selection wash: paints the real building footprint (and its extruded walls)
 * in translucent amber so the illustrated roof, windows and shading below stay
 * readable — a highlighted area, not a solid yellow block.
 */
function HighlightShape({ b }: { b: Building }) {
  const h = b.h * H_SCALE;
  const ground = b.p.map(([x, y]) => `${x},${y}`).join(" ");
  const roof = b.p.map(([x, y]) => `${x},${y - h}`).join(" ");

  return (
    <g>
      {/* walls */}
      {b.p.slice(0, -1).map(([ax, ay], i) => {
        const [bx, by] = b.p[i + 1];
        return (
          <polygon
            key={i}
            points={`${ax},${ay} ${bx},${by} ${bx},${by - h} ${ax},${ay - h}`}
            fill="#F5B944"
            fillOpacity={0.32}
          />
        );
      })}
      {/* roof area */}
      <polygon points={roof} fill="#F5B944" fillOpacity={0.45} stroke="#E08A11" strokeWidth={0.9} />
      {/* ground outline so the area reads clearly on the illustration */}
      <polygon
        points={ground}
        fill="none"
        stroke="#E08A11"
        strokeWidth={0.9}
        strokeDasharray="3 2"
      />
    </g>
  );
}



/** Hand-drawn highlight area: polygon points in the same screen units as pins. */
export type AreaPoly = [number, number][];

/** Look of the highlight wash — editable per marker in the admin map editor. */
export type AreaStyle = {
  /** Fill colour, hex. */
  color?: string;
  /** 0–1 fill opacity of the top face. */
  opacity?: number;
  /** Outline thickness in screen units. */
  stroke?: number;
  /** Extrusion height for the 3D block (0 = flat wash). */
  height?: number;
};

export const DEFAULT_AREA_STYLE: Required<AreaStyle> = {
  color: "#F5B944",
  opacity: 0.45,
  stroke: 0.9,
  height: 8,
};

function resolveStyle(s?: AreaStyle | null): Required<AreaStyle> {
  return { ...DEFAULT_AREA_STYLE, ...(s ?? {}) };
}

/** Darken a hex colour by `amount` (0–1) for the extruded side walls. */
function shade(hex: string, amount: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const f = 1 - amount;
  const r = Math.round(((n >> 16) & 255) * f);
  const g = Math.round(((n >> 8) & 255) * f);
  const b = Math.round((n & 255) * f);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/**
 * Custom highlight for a hand-drawn area, extruded into a soft 3D block so the
 * painted zone reads as a volume on the isometric illustration instead of a
 * flat sticker. Side walls are shaded, the top face carries the fill colour.
 */
function CustomAreaShape({ area, style }: { area: AreaPoly; style?: AreaStyle | null }) {
  const s = resolveStyle(style);
  const h = Math.max(0, s.height);
  const ground = area.map(([x, y]) => `${x},${y}`).join(" ");
  const roof = area.map(([x, y]) => `${x},${y - h}`).join(" ");
  const dark = shade(s.color, 0.28);
  const darker = shade(s.color, 0.45);

  return (
    <g>
      {/* soft ground shadow */}
      <polygon points={ground} fill={darker} fillOpacity={s.opacity * 0.28} />
      {/* extruded side walls, alternating light so edges stay readable */}
      {h > 0
        ? area.map(([ax, ay], i) => {
            const [bx, by] = area[(i + 1) % area.length];
            const facingLeft = bx < ax;
            return (
              <polygon
                key={i}
                points={`${ax},${ay} ${bx},${by} ${bx},${by - h} ${ax},${ay - h}`}
                fill={facingLeft ? darker : dark}
                fillOpacity={s.opacity * 0.85}
              />
            );
          })
        : null}
      {/* top face */}
      <polygon
        points={roof}
        fill={s.color}
        fillOpacity={s.opacity}
        stroke={s.color === "none" ? "none" : shade(s.color, 0.35)}
        strokeWidth={s.stroke}
        strokeLinejoin="round"
      />
      {/* ground outline keeps the footprint crisp */}
      <polygon
        points={ground}
        fill="none"
        stroke={shade(s.color, 0.35)}
        strokeWidth={Math.max(0.3, s.stroke * 0.7)}
        strokeDasharray="3 2"
        strokeLinejoin="round"
      />
    </g>
  );
}

/** Marker placed by hand in the admin map editor (screen units, not lat/lng). */
export type ManualPin = {
  id: string;
  n: number;
  label: string;
  address?: string | null;
  slug?: string | null;
  category?: string | null;
  x: number;
  y: number;
  /** Optional hand-drawn highlight area; falls back to the building footprint. */
  area?: AreaPoly | null;
  /** Optional per-marker look of the highlight wash. */
  areaStyle?: AreaStyle | null;
  /** Optional photo shown on the place card. */
  image?: string | null;
  /** District hub / main Train Factory marker — drawn differently from businesses. */
  anchor?: boolean;
};


/** Perpendicular distance of p from segment a→b. */
function segDist(p: [number, number], a: [number, number], b: [number, number]): number {
  const [px, py] = p;
  let [x, y] = a;
  let dx = b[0] - x;
  let dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) [x, y] = b;
    else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }
  dx = px - x;
  dy = py - y;
  return Math.hypot(dx, dy);
}

/** Ramer–Douglas–Peucker: turn a jittery freehand stroke into clean corners. */
export function simplifyPath(points: AreaPoly, tolerance = 2.2): AreaPoly {
  if (points.length < 3) return points;
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop() as [number, number];
    let maxD = 0;
    let idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = segDist(points[i], points[s], points[e]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx > 0 && maxD > tolerance) {
      keep[idx] = true;
      stack.push([s, idx], [idx, e]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/** Chaikin smoothing on a closed ring — rounds the corners of a freehand loop. */
export function smoothClosed(points: AreaPoly, passes = 1): AreaPoly {
  let ring = points;
  for (let p = 0; p < passes; p++) {
    if (ring.length < 4) break;
    const out: AreaPoly = [];
    for (let i = 0; i < ring.length; i++) {
      const [ax, ay] = ring[i];
      const [bx, by] = ring[(i + 1) % ring.length];
      out.push([ax + (bx - ax) * 0.25, ay + (by - ay) * 0.25]);
      out.push([ax + (bx - ax) * 0.75, ay + (by - ay) * 0.75]);
    }
    ring = out;
  }
  return ring.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
}

/**
 * Freehand stroke → tidy closed area: drop duplicate samples, simplify the
 * jitter away, then round the corners so the saved shape looks deliberate.
 */
export function tidyFreehand(stroke: AreaPoly): AreaPoly {
  const dedup: AreaPoly = [];
  for (const p of stroke) {
    const last = dedup[dedup.length - 1];
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 1.2) dedup.push(p);
  }
  if (dedup.length < 3) return dedup;
  const simple = simplifyPath(dedup, 2.4);
  const ring = simple.length > 40 ? simplifyPath(simple, 4) : simple;
  return smoothClosed(ring, ring.length > 6 ? 1 : 2);
}

/** Screen-unit position of a click inside the isometric stage. */
export function pointFromClient(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): { x: number; y: number; building: number } {
  const x = VIEW.x + ((clientX - rect.left) / rect.width) * VIEW.w;
  const y = VIEW.y + ((clientY - rect.top) / rect.height) * VIEW.h;
  return { x, y, building: buildingAt(x, y) };
}

export function IsometricDistrictMap({
  pins,
  manualPins,
  districtName,
  height = 520,
  editable,
  onPickPoint,
  selectedId,
  onSelect,
  drawing,
  draftArea,
  freehand,
  draftStyle,
  onFreehandStroke,
}: {
  pins: IsoPin[];
  manualPins?: ManualPin[];
  districtName: string;
  height?: number;
  /** Admin editor: clicking anywhere on the map reports a placement point. */
  editable?: boolean;
  onPickPoint?: (p: { x: number; y: number; building: number }) => void;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Area tool: clicks add polygon corners instead of selecting markers. */
  drawing?: boolean;
  draftArea?: AreaPoly;
  /** Freehand mode: drag to paint the outline, released stroke is auto-tidied. */
  freehand?: boolean;
  /** Live style applied to the draft preview while editing. */
  draftStyle?: AreaStyle | null;
  onFreehandStroke?: (points: AreaPoly) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [internalActive, setInternalActive] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [width, setWidth] = useState(0);

  const active = selectedId !== undefined ? selectedId : internalActive;
  const setActive = (next: string | null) => {
    if (onSelect) onSelect(next);
    if (selectedId === undefined) setInternalActive(next);
  };

  // Measure the real rendered width so pin size and label density adapt to
  // phones without relying on CSS breakpoints that the SVG stage can't see.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const venuePins = useMemo(() => project(pins), [pins]);
  const manual = useMemo<Placed[]>(
    () =>
      (manualPins ?? []).map((m) => ({
        id: m.id,
        label: m.label,
        address: m.address ?? null,
        slug: m.slug ?? undefined,
        category: m.category ?? null,
        image: m.image ?? null,
        lat: 0,
        lng: 0,
        kind: m.anchor ? "do" : kindOf(m.category),
        color: m.anchor ? "#0F2E44" : PIN_STYLE[kindOf(m.category)].color,
        x: m.x,
        y: m.y,
        n: m.n,
        building: buildingAt(m.x, m.y),
        anchor: !!m.anchor,
      })),
    [manualPins],
  );
  // Admin-placed markers win; then real venue coordinates; the halls are the
  // last resort so the map is always explorable.
  const placed = manual.length > 0 ? manual : venuePins.length > 0 ? venuePins : editable ? [] : HALL_MARKERS;

  const activePin = placed.find((p) => p.id === active) ?? null;
  // Hand-drawn area wins over the auto building footprint for that marker.
  const activeArea = useMemo(() => {
    const found = (manualPins ?? []).find((m) => m.id === active)?.area;
    return found && found.length >= 3 ? found : null;
  }, [manualPins, active]);
  const activeAreaStyle = useMemo(
    () => draftStyle ?? (manualPins ?? []).find((m) => m.id === active)?.areaStyle ?? null,
    [manualPins, active, draftStyle],
  );

  // Freehand stroke being painted right now (screen units).
  const [stroke, setStroke] = useState<AreaPoly>([]);
  const strokeRef = useRef<AreaPoly>([]);
  const painting = useRef(false);

  const freehandHandlers =
    editable && drawing && freehand
      ? {
          onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            const r = e.currentTarget.getBoundingClientRect();
            const p = pointFromClient(r, e.clientX, e.clientY);
            painting.current = true;
            strokeRef.current = [[p.x, p.y]];
            setStroke(strokeRef.current);
            e.currentTarget.setPointerCapture(e.pointerId);
          },
          onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!painting.current) return;
            const r = e.currentTarget.getBoundingClientRect();
            const p = pointFromClient(r, e.clientX, e.clientY);
            strokeRef.current = [...strokeRef.current, [p.x, p.y]];
            setStroke(strokeRef.current);
          },
          onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!painting.current) return;
            painting.current = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              /* pointer already released */
            }
            const tidy = tidyFreehand(strokeRef.current);
            strokeRef.current = [];
            setStroke([]);
            if (tidy.length >= 3) onFreehandStroke?.(tidy);
          },
        }
      : {};

  // Match the optimized artwork's real 1920 × 1182 dimensions. The previous
  // ratio made the centered image shorter than its frame, leaving white bands.
  const stageRatio = 1920 / 1182;
  const narrow = width > 0 && width < 640;
  // Fill the container width exactly so there is no dead space on the sides;
  // the box height simply follows the illustration's aspect ratio.
  const boxHeight = width > 0 ? Math.round(width / stageRatio) : height;
  const inv = narrow ? Math.max(0.68, Math.min(1, width / 560)) : 1;
  


  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm"
      style={{ height: boxHeight }}
    >
      <div
        ref={containerRef}
        className="size-full touch-manipulation select-none bg-white [-webkit-tap-highlight-color:transparent]"
      >
        <div className="relative size-full">
          <div
            ref={stageRef}
            onClick={
              editable && onPickPoint
                ? (e) => {
                    if (freehand && drawing) return;
                    if (!drawing && (e.target as HTMLElement).closest("button")) return;
                    const r = e.currentTarget.getBoundingClientRect();
                    onPickPoint(pointFromClient(r, e.clientX, e.clientY));
                  }
                : undefined
            }
            {...freehandHandlers}
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${
              editable ? "cursor-crosshair" : ""
            } ${drawing && freehand ? "touch-none" : ""}`}
            style={{
              width: "100%",
              aspectRatio: `${stageRatio}`,
            }}
          >
            {/* Map layer stays static — the venue card floats next to the clicked marker. */}
            <div className="absolute inset-0">
              {/* Rendered isometric illustration of the Konepaja block */}
              <img
                src={isoMap.url}
                alt={`Isometric map of ${districtName}`}
                className="pointer-events-none absolute inset-0 size-full object-cover"
                draggable={false}
              />
              {/* Highlight overlay: the selected building's real footprint is
                  painted with a translucent wash so the illustration underneath
                  stays visible instead of being covered by a solid block. */}
              <svg
                viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
                className="pointer-events-none absolute inset-0 size-full mix-blend-multiply"
                aria-hidden="true"
              >
                {activeArea
                  ? <CustomAreaShape area={activeArea} style={activeAreaStyle} />
                  : SORTED_BUILDINGS.map((b, i) =>
                      i === activePin?.building ? <HighlightShape key={i} b={b} /> : null,
                    )}
              </svg>

              {/* Area tool: live preview of the polygon or freehand stroke */}
              {drawing && (stroke.length > 1 || (draftArea && draftArea.length > 0)) ? (
                <svg
                  viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
                  className="pointer-events-none absolute inset-0 z-30 size-full"
                  aria-hidden="true"
                >
                  {stroke.length > 1 ? (
                    <>
                      <polygon
                        points={stroke.map(([x, y]) => `${x},${y}`).join(" ")}
                        fill={resolveStyle(draftStyle).color}
                        fillOpacity={resolveStyle(draftStyle).opacity * 0.5}
                      />
                      <polyline
                        points={stroke.map(([x, y]) => `${x},${y}`).join(" ")}
                        fill="none"
                        stroke={shade(resolveStyle(draftStyle).color, 0.35)}
                        strokeWidth={Math.max(0.8, resolveStyle(draftStyle).stroke)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  ) : draftArea && draftArea.length >= 3 ? (
                    <CustomAreaShape area={draftArea} style={draftStyle} />
                  ) : draftArea ? (
                    <polyline
                      points={draftArea.map(([x, y]) => `${x},${y}`).join(" ")}
                      fill="none"
                      stroke={shade(resolveStyle(draftStyle).color, 0.35)}
                      strokeWidth={1}
                      strokeDasharray="3 2"
                    />
                  ) : null}
                  {stroke.length > 1
                    ? null
                    : (draftArea ?? []).map(([x, y], i) => (
                        <circle key={i} cx={x} cy={y} r={1.8} fill="#0F2E44" stroke="#fff" strokeWidth={0.6} />
                      ))}
                </svg>
              ) : null}


              {/* Venue pins at their real coordinates. While drawing an area
                  only the marker being edited stays visible so other numbers
                  don't get in the way of corner placement. */}
              {placed
                .filter((p) => !drawing || p.id === active)
                .map((p) => {
                  const isActive = active === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActive(active === p.id ? null : p.id)}
                      style={{
                        left: `${pctX(p.x)}%`,
                        top: `${pctY(p.y)}%`,
                        transform: `translate(-50%, -50%) scale(${Math.max(0.5, inv)})`,
                        padding: `${Math.round(9 / Math.max(0.5, inv))}px`,
                      }}
                      className={`absolute grid touch-manipulation place-items-center ${
                        isActive ? "z-30" : "z-20"
                      }`}
                      aria-label={`${p.n}. ${p.label}`}
                      aria-pressed={isActive}
                      title={p.label}
                    >
                      <span
                        className={`flex items-center justify-center border-2 font-bold shadow-md transition-transform hover:scale-110 active:scale-95 ${
                          p.anchor
                            ? `size-9 rounded-lg text-[10px] tracking-wide ${
                                isActive
                                  ? "border-white bg-[#0d6efd] text-white"
                                  : "border-white bg-[#0F2E44] text-white"
                              }`
                            : `size-7 rounded-full text-[11px] ${
                                isActive
                                  ? "border-white bg-[#0F2E44] text-white"
                                  : "border-[#1B2330] bg-white text-[#1B2330]"
                              }`
                        }`}
                      >
                        {p.anchor ? "TF" : p.n}
                      </span>
                    </button>
                  );
                })}

              {/* Highlighted area label for the selected number */}
              {activePin ? (
                <span
                  style={{
                    left: `${pctX(activePin.x)}%`,
                    top: `${pctY(activePin.y)}%`,
                    transform: `translate(-50%, -190%) scale(${Math.max(0.55, inv)})`,
                    textTransform: "none",
                  }}
                  className="pointer-events-none absolute z-40 flex max-w-[180px] items-center gap-1.5 truncate rounded-full bg-[#0F2E44]/95 px-3 py-1 font-heading text-[11px] font-bold text-white shadow-lg ring-1 ring-white/25 backdrop-blur-[2px] sm:max-w-[260px] sm:text-[12px]"
                >
                  <span className="grid size-4 shrink-0 place-items-center rounded-full bg-white/20 text-[9px] font-black">
                    {activePin.n}
                  </span>
                  <span className="truncate">{activePin.label}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>


      {/* Selected venue card — contextual on desktop, bottom sheet on mobile. */}
      {activePin && !editable
        ? (() => {
            const isMobile = width > 0 && width < 640;

            const CardBody = () => (
              <>
                <div className="flex items-start gap-3 p-2.5 sm:p-3">
                  {activePin.image ? (
                    <button
                      type="button"
                      onClick={() => setLightbox(activePin.image ?? null)}
                      aria-label={`View photo of ${activePin.label}`}
                      className="group relative size-14 shrink-0 overflow-hidden rounded-xl border border-white/15 sm:size-16"
                    >
                      <img
                        src={activePin.image}
                        alt={activePin.label}
                        loading="lazy"
                        decoding="async"
                        className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <span className="absolute inset-0 grid place-items-center bg-navy/40 text-white opacity-0 transition group-hover:opacity-100">
                        <Expand className="size-4" />
                      </span>
                    </button>
                  ) : (
                    <span className="grid size-14 shrink-0 place-items-center rounded-xl bg-white/10 font-heading text-lg font-black text-white/80 sm:size-16">
                      {activePin.n}
                    </span>
                  )}

                  <div className="min-w-0 flex-1 pr-6">
                    <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/50">
                      {venuePins.length > 0 ? "Selected place" : `Building ${activePin.n}`}
                    </p>
                    <h3
                      className="mt-0.5 truncate font-heading text-[15px] leading-tight text-white sm:text-base"
                      style={{ textTransform: "none" }}
                      title={activePin.label}
                    >
                      {activePin.label}
                    </h3>
                    {activePin.address ? (
                      <p
                        style={{ textTransform: "none" }}
                        className="mt-0.5 line-clamp-1 text-[11px] leading-snug text-white/65"
                      >
                        {activePin.address}
                      </p>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => setActive(null)}
                    aria-label="Close"
                    className="absolute right-1 top-1 grid size-9 place-items-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white"
                  >
                    <X className="size-4" />
                  </button>
                </div>

                {activePin.slug ? (
                  <Link
                    to="/place/$id"
                    params={{ id: activePin.slug }}
                    style={{ textTransform: "none" }}
                    className="flex items-center justify-between border-t border-white/10 bg-white/5 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-white/10"
                  >
                    View venue
                    <ArrowUpRight className="size-4" />
                  </Link>
                ) : null}
              </>
            );

            if (isMobile) {
              return (
                <div className="absolute inset-x-3 bottom-3 z-50 overflow-hidden rounded-2xl border border-white/10 bg-[#0F2E44]/95 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl">
                  <CardBody />
                </div>
              );
            }

            const px = pctX(activePin.x);
            const py = pctY(activePin.y);
            const cardW = Math.min(340, (width || 340) * 0.92);
            const half = cardW / 2;
            // Clamp horizontally so the card never spills past the map edges.
            const leftPx =
              width > 0
                ? Math.min(Math.max((px / 100) * width, half + 8), width - half - 8)
                : undefined;
            // Pins in the top half open the card below them; lower pins above.
            const below = py < 52;
            return (
              <div
                className="absolute z-40 w-[min(340px,92%)] overflow-hidden rounded-2xl border border-white/10 bg-[#0F2E44]/95 shadow-2xl ring-1 ring-white/10 backdrop-blur-xl"
                style={{
                  left: leftPx,
                  top: below ? `${py}%` : undefined,
                  bottom: below ? undefined : `${100 - py}%`,
                  transform: below ? "translate(-50%, 16px)" : "translate(-50%, calc(-100% - 16px))",
                }}
              >
                <CardBody />
              </div>
            );
          })()
        : null}


      {/* Photo lightbox */}
      {lightbox ? (
        <div
          className="absolute inset-0 z-50 grid place-items-center bg-navy/70 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt="Place photo"
            className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close photo"
            className="absolute right-3 top-3 grid size-9 place-items-center rounded-full bg-white/90 text-navy"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default IsometricDistrictMap;
